import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, rem, Text, Tooltip } from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { useBookmark } from '../../context';
import { BookmarkLabelEvent } from '../../types/event';
import { LabelPill } from '../labels/label-pill';
import { Suggestion } from './lib/entities';
import { SuggestionIcon } from './suggestion-icon';

interface SuggestionPillProps {
	suggestion: Suggestion;
}
export const SuggestionPill: FC<SuggestionPillProps> = ({ suggestion }) => {
	const { suggestions, setSuggestions, labelsIds, setLabelsIds } = useBookmark();

	const { t } = useTranslation('bookmarks');
	const { track } = useAnalytics();

	const isSelected = (suggestion: Suggestion): boolean => {
		if (suggestion.isSuggested) {
			return suggestions?.includes(suggestion.name) || false;
		}
		if (!suggestion.id || !labelsIds) return false;
		return labelsIds?.includes(suggestion.id) || false;
	};

	const handleSelection = (suggestion: Suggestion) => {
		if (isSelected(suggestion)) {
			const filterFn = suggestion.isSuggested ? (s: string) => s !== suggestion.name : (l: string) => l !== suggestion.id;

			if (suggestion.isSuggested) {
				track(BookmarkLabelEvent.AiSuggestedLabelRemoved, {
					name: suggestion.name,
				});
				setSuggestions((suggestions || []).filter(filterFn));
			} else {
				track(BookmarkLabelEvent.ExistingLabelRemoved, {
					id: suggestion.id,
					name: suggestion.name,
				});
				setLabelsIds((labelsIds || []).filter(filterFn) as Array<string>);
			}
		} else {
			if (suggestion.isSuggested) {
				track(BookmarkLabelEvent.AiSuggestedLabelSelected, {
					name: suggestion.name,
				});
				setSuggestions([...(suggestions || []), suggestion.name]);
			} else {
				track(BookmarkLabelEvent.ExistingLabelSelected, {
					id: suggestion.id,
					name: suggestion.name,
				});
				setLabelsIds([...(labelsIds || []), suggestion.id] as Array<string>);
			}
		}
	};

	return (
		<LabelPill
			key={suggestion.name}
			h={rem(34)}
			backgroundColor="primary.bgAlpha"
			borderColor="transparent"
			_hover={{
				bg: 'primary.bgAlphaHover',
			}}
			handleSelection={() => handleSelection(suggestion)}
		>
			<Tooltip label={isSelected(suggestion) ? t('suggestions.remove_label') : t('suggestions.add_label')} placement="top" hasArrow key={suggestion.name}>
				<Flex alignItems="center" w="full" gap={0.5}>
					<SuggestionIcon isSelected={isSelected(suggestion)} isSuggested={suggestion.isSuggested} />
					<Text fontSize="sm" fontWeight="medium" color={isSelected(suggestion) ? 'primary.fgStrong' : 'secondary.fg'}>
						{suggestion.name}
					</Text>
				</Flex>
			</Tooltip>
		</LabelPill>
	);
};
